import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

// SEC-005 — the JWT lives in an httpOnly `verda_auth` cookie set by the backend.
// JS never sees the token; we only cache the (non-sensitive) user profile in
// localStorage to avoid a login flash on refresh. `/auth/me` is the source of
// truth — if the cookie is gone/expired the server returns 401 and we clear.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hv_user") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Legacy cleanup: prior versions stored the token in localStorage.
    try { localStorage.removeItem("hv_token"); } catch { /* storage disabled */ }

    let cancelled = false;
    api.get("/auth/me")
      .then((r) => {
        if (cancelled) return;
        setUser(r.data);
        try { localStorage.setItem("hv_user", JSON.stringify(r.data)); } catch { /* storage disabled */ }
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        try { localStorage.removeItem("hv_user"); } catch { /* storage disabled */ }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // login(user) — cookie is already set by the backend on the login response.
  // NOTE: signature kept 2-arg for callers that still pass (token, user); the
  // token argument is ignored and can be safely removed once all callers migrate.
  const login = useCallback((tokenOrUser, maybeUser) => {
    const u = maybeUser !== undefined ? maybeUser : tokenOrUser;
    try { localStorage.setItem("hv_user", JSON.stringify(u)); } catch { /* storage disabled */ }
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* server unreachable — clear locally anyway */ }
    try {
      localStorage.removeItem("hv_user");
      localStorage.removeItem("hv_active_member"); // avoid leaking prior user's active member id
    } catch { /* storage disabled */ }
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
